// Small shared pieces of the account panel: the rail's icons, the reach marks, and a labelled
// section. Inline SVG, drawn in currentColor, so they follow the tokens in both themes.
import type { ReactNode } from 'react';
import type { PanelTab } from './panelFacts.ts';

export function EnvelopeIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="1.8" y="3.3" width="12.4" height="9.4" rx="1.6" />
      <path d="m2.4 4.3 5.6 4.3 5.6-4.3" />
    </svg>
  );
}

const RAIL_PATHS: Record<Exclude<PanelTab, 'Scout'>, ReactNode> = {
  Company: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M10 21v-4h4v4" /></>,
  Outreach: <><path d="M4 5h16v11H8l-4 4z" /><path d="M8 9h8M8 12h5" /></>,
  Research: <><circle cx="11" cy="11" r="6" /><path d="m20 20-4.5-4.5" /></>,
  History: <><path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" /><path d="M3 4v4h4M12 8v4l3 2" /></>,
};

/** The rail's icon for a tab. Scout's is the beagle mark, drawn by CSS (scout-mark.css). */
export function RailIcon({ tab }: { tab: PanelTab }) {
  if (tab === 'Scout') return <span className="railscout" aria-hidden="true" />;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      {RAIL_PATHS[tab]}
    </svg>
  );
}

/** Email and LinkedIn marks: filled when we hold one, dashed when we do not. */
export function ReachMarks({ email, linkedin }: { email: boolean; linkedin: boolean }) {
  return (
    <span className="reach">
      <span className={'reachmark email' + (email ? '' : ' off')} title={email ? 'Email on file' : 'No email'}>
        <EnvelopeIcon />
      </span>
      <span className={'reachmark linkedin' + (linkedin ? '' : ' off')} title={linkedin ? 'LinkedIn on file' : 'No LinkedIn'}>
        in
      </span>
    </span>
  );
}

/** A labelled stretch of a tab. Renders nothing without children. */
export function PanelSection({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  if (!children) return null;
  return (
    <section className="psection">
      <h3 className="psectionhead"><span>{title}</span>{aside ? <span className="psectionaside">{aside}</span> : null}</h3>
      {children}
    </section>
  );
}
